import React from 'react';
import { Button } from '@attentive-platform/stem-ui';

import { cellLabel, customerDaysForCard } from './resolveRecord';
import { TatConsoleStyles as S } from './styles';

const RecordTurnaroundBlock = ({
  cfg,
  resolved,
  custom,
  canAct,
  onSet,
  onEdit,
  onRemove,
  iso,
  /** True when trade.account_preset?.tat is present. */
  hasAccountPreset,
  presetDetached
}) => {
  const isCustom = !!custom;
  const hasPreset = !!hasAccountPreset;

  // Empty only when there is no account_preset card and no custom_card.
  if (!hasPreset && !isCustom) {
    return (
      <S.Block>
        <S.Eyebrow>Turnaround</S.Eyebrow>
        <S.Foot>No turnaround card on this trade.</S.Foot>
      </S.Block>
    );
  }

  // Custom card with no synthesised cfg (preset empty / detached) — still show + Remove.
  if (isCustom && (!cfg?.cards?.length || !resolved?.card)) {
    return (
      <S.Block>
        <S.TopRow>
          <S.Left>
            <S.Eyebrow>Turnaround</S.Eyebrow>
            <S.TitleRow>
              <S.Title>
                Custom card, set by {custom.by} on {custom.date}
              </S.Title>
            </S.TitleRow>
            {custom.reason ? <S.Reason>&ldquo;{custom.reason}&rdquo;</S.Reason> : null}
            {custom.inPlaceOf ? (
              <S.InPlaceOf>
                In place of: <strong style={{ fontWeight: 500 }}>{custom.inPlaceOf}</strong>
              </S.InPlaceOf>
            ) : null}
            <S.Warn>
              <S.WarnDot />
              Account preset is no longer attached — custom card still governs submissions
            </S.Warn>
          </S.Left>
          {canAct ? (
            <S.Actions>
              <Button variant='text' color='primary' size='medium' onClick={onRemove}>
                Remove custom card
              </Button>
            </S.Actions>
          ) : null}
        </S.TopRow>
        <S.Foot>Customer card, business days. The internal card never renders here.</S.Foot>
      </S.Block>
    );
  }

  if (!cfg?.cards?.length || !resolved?.card) return null;

  const { card, preset, exact } = resolved;
  const values = isCustom
    ? custom.values
    : customerDaysForCard(card, cfg.ranges.length, iso);
  const warn = isCustom && custom.resolvedCardId && custom.resolvedCardId !== card.id;
  const line1 = isCustom
    ? `Custom card, set by ${custom.by} on ${custom.date}`
    : `Card: ${card.name}`;
  const tier = isCustom
    ? ''
    : cfg.singleCard
      ? `the only card for this trade`
      : card.of != null
        ? `card ${card.tier} of ${card.of} for this trade`
        : `ladder position ${card.tier}`;
  const presetLine = exact
    ? `Preset: ${preset.name}`
    : `Preset: customised from ${preset.name}`;
  const detached = presetDetached || (isCustom && !hasPreset);

  return (
    <S.Block>
      <S.TopRow>
        <S.Left>
          <S.Eyebrow>Turnaround</S.Eyebrow>
          <S.TitleRow>
            <S.Title>{line1}</S.Title>
            {tier ? <S.Tier>{tier}</S.Tier> : null}
          </S.TitleRow>
          {isCustom ? (
            <>
              <S.Reason>&ldquo;{custom.reason}&rdquo;</S.Reason>
              <S.InPlaceOf>
                In place of: <strong style={{ fontWeight: 500 }}>{custom.inPlaceOf || card.name}</strong>
              </S.InPlaceOf>
            </>
          ) : null}
          {detached ? (
            <S.Warn>
              <S.WarnDot />
              Account preset is no longer attached — custom card still governs submissions
            </S.Warn>
          ) : (
            <S.PresetLine>{presetLine}</S.PresetLine>
          )}
          {warn ? (
            <S.Warn>
              <S.WarnDot />
              Resolved card changed since this custom card was set
            </S.Warn>
          ) : null}
        </S.Left>
        {canAct ? (
          <S.Actions>
            {/* Set requires a live account_preset; custom_card alone is not enough. */}
            {!isCustom && hasPreset ? (
              <Button variant='outlined' color='primary' size='medium' onClick={onSet}>
                Set custom card
              </Button>
            ) : null}
            {isCustom ? (
              <>
                <Button variant='outlined' color='primary' size='medium' onClick={onEdit}>
                  Edit custom card
                </Button>
                <Button variant='text' color='primary' size='medium' onClick={onRemove}>
                  Remove custom card
                </Button>
              </>
            ) : null}
          </S.Actions>
        ) : null}
      </S.TopRow>

      <S.Cells $cols={cfg.ranges.length} aria-label='Customer business days by sheet range'>
        {cfg.ranges.map((lab, i) => {
          const v = values[i];
          return (
            <S.Cell key={lab}>
              <S.CellRange>{lab}</S.CellRange>
              <S.CellVal $quote={v == null}>{cellLabel(v)}</S.CellVal>
            </S.Cell>
          );
        })}
      </S.Cells>
      <S.Foot>Customer card, business days. The internal card never renders here.</S.Foot>
    </S.Block>
  );
};

export default RecordTurnaroundBlock;
